import React from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Carousel, {
	ICarouselProps,
	CarouselItem,
} from 'tbk-components/src/components/Carousel';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image, { IImage } from 'tbk-components/src/components/Image';

export interface IHeroBanner {
	/**
	 * Banner Heading
	 */
	heading?: string;
	/**
	 * Banner Text
	 * @control textarea
	 */
	tagline?: string;
	link?: ILink;
	/**
	 * Banner Image | Image should be 1440px X 683px
	 */
	image?: IImage;
	/**
	 * Image size | 'fill' will make the image fill the entire banner, leaving no empty space. 'fit' will make the image fit within the height or width of the banner.
	 */
	imageSize?: 'fill' | 'fit';
	/**
	 * Image position
	 */
	imagePosition?:
		| 'top left'
		| 'top center'
		| 'top right'
		| 'left'
		| 'center'
		| 'right'
		| 'bottom left'
		| 'bottom center'
		| 'bottom right';
}

export interface IRotatingHeroBannerProps extends IBaseElementProps {
	heroBanners?: IHeroBanner[];
	/**
	 * Enable Banner Autoplay
	 */
	enableAutoplay?: boolean;
	/**
	 * Banner Carousel Auto Play Speed in Milliseconds
	 */
	autoplayDuration?: string;
}

/**
 * Rotating Hero Banner
 * @block
 * @requiresHooks
 * @icon format-image
 */
const RotatingHeroBanner: React.FC<IRotatingHeroBannerProps> = ({
	className,
	classNames = [],
	heroBanners = [],
	enableAutoplay,
	autoplayDuration,
}) => {
	const carouselOptions: any = {};

	//
	const handleTabbing = (glide) => {
		const handler = () => {
			document
				.querySelectorAll('.glide__slide button, .glide__slide a')
				.forEach((el) => {
					el.setAttribute('tabindex', '-1');
				});

			document
				.querySelectorAll(
					'.glide__slide--active button, .glide__slide--active a',
				)
				.forEach((el) => {
					el.removeAttribute('tabindex');
				});
		};

		glide.on('mount.after', handler);
		glide.on('move.after', handler);
	};

	if (enableAutoplay) {
		carouselOptions.autoplay = autoplayDuration;
	}
	return (
		<Carousel
			id="rotating-hero-banner-carousel"
			options={carouselOptions}
			className={className || cn('glide', 'rotating-hero-banner', ...classNames)}
			onMount={handleTabbing}
		>
			{heroBanners.map((heroBanner, i) => (
				<CarouselItem key={i}>
					<div className="rotating-hero-banner__banner">
						<div className="container-fluid">
							<div className="row">
								<div className="hero-banner__background-overlay">
									<div className="col-12 col-lg-6">
										<div className="hero-banner__content">
											{heroBanner.heading && (
												<h1 className="hero-banner__heading">
													{heroBanner.heading}
												</h1>
											)}
											{heroBanner.tagline && (
												<p className="hero-banner__tagline">
													{heroBanner.tagline}
												</p>
											)}
											{heroBanner?.link?.label &&
												heroBanner.link.label.length >
													0 && (
													<Button
														className="btn btn-secondary btn-lg"
														{...heroBanner.link}
													/>
												)}
										</div>
									</div>
									<div className="d-none d-lg-block col-lg-6" />
								</div>
								<div
									className={cn(
										'hero-banner__background-image',
										heroBanner.imagePosition,
										heroBanner.imageSize,
									)}
									style={
										heroBanner.image
											? {
													backgroundImage: `url("${heroBanner.image.src}")`,
											  }
											: {}
									}
								/>
							</div>
						</div>
					</div>
				</CarouselItem>
			))}
		</Carousel>
	);
};

export default RotatingHeroBanner;
